import { Fragment, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps, type CSSProperties } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Clickable from 'components/Common/Clickable'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import type { PhysicalHorizontalDirection, ScrollPosition } from 'LuxKit/Carousel/utils'
import {
  getLogicalScrollDeltaForPhysicalHorizontalDirection,
  getLogicalScrollLeft,
  scrollByLogicalPosition,
  setLogicalScrollLeft,
} from 'LuxKit/Carousel/utils'
import useTextDirection from 'hooks/useTextDirection'
import useScrollEndsObserver from 'hooks/useScrollEndsObserver'
import useResizeObserver from 'hooks/useResizeObserver'
import MaldivesCharterTripOptionsTableExpandedRow from './MaldivesCharterTripOptionsTableExpandedRow'
import Group from 'components/utils/Group'
import focusStyle from 'styles/utils/focusStyle'
import zIndex from 'styles/tools/z-index'

import { trackClientEvent } from 'analytics/analytics'
import { CHARTER_CALENDAR_PAGE_SIZE } from 'constants/charterFlight'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { FlightsClassTypes, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { INTL_DAY_NAME_DAY_MONTH, INTL_SHORT_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import { CHARTER_SELECTABLE_CASH_CABINS, expandMaldivesCharterFareClassesWithRewards, getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'

import { useCalendarItemData } from '../hooks/useCalendarItemData'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import { useSeatEligiblePassengers } from '../hooks/utils'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import { computeFareClassCellPrice, dateHasAvailableCashFare, dateHasInfiniteMemberPrice } from './maldivesCharterTripOptionsTablePricing'
import { hasReturnFareFree as hasReturnFareFreeHelper } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { shouldShowReturnFareFreeCalendarLabel } from './returnFareFreeCalendarVisibility'
import type { CalendarItemBundleData, DateItem, DateRange } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import {
  STICKY_DATE_COL_WIDTH,
  FARE_COL_MIN_WIDTH,
  verticalCellDivider,
  stickyDateColumn,
} from './maldivesCharterTripOptionsTableStyles'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { MaldivesFnfSpecialOfferLabel } from './MaldivesFnfSpecialOffer'
import { isTripOptionsTableScrollArrowHidden } from './maldivesCharterTripOptionsTableScrollArrows'

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
  fareClass?: FareClassWithReward
}

interface Props {
  currentMonthTripOptions: Array<DateItem>
  selectedMonth: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  luxPlusEnabled: boolean
  selectedOptionId?: string
  onOptionSelect: (option: TripOption) => void
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /** Per-cabin lowest hotel by date, used by each per-cabin column so its hotel total
   *  reflects only offers that allow that cabin — independent of the page-level cabin. */
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  /** When true, hide rows whose dates lack reward-seat availability for every leg. */
  rewardSeatsOnly?: boolean
  /** When true, hide rows whose dates lack an Infinite Member (Friends & Family) fare. */
  infiniteMemberOnly?: boolean
  feederOriginAirportCode?: string
  /**
   * Optional cabin filter from the modal's class dropdown. When set, only this base cabin's
   * columns (cash + reward) are rendered; other cabins are hidden entirely.
   */
  cabinFilter?: FlightsClassTypes | null
}

function getHotelPriceByCabinForDate(
  hotelPriceByCabinAndDate: Props['hotelPriceByCabinAndDate'],
  checkIn: string,
) {
  if (!hotelPriceByCabinAndDate) return undefined
  const result: Partial<Record<FlightsClassTypes, MaldivesCabinHotelEntry>> = {}
  for (const cabin of CHARTER_SELECTABLE_CASH_CABINS) {
    const entry = hotelPriceByCabinAndDate[cabin]?.[checkIn]
    if (entry) result[cabin] = entry
  }
  return result
}

const pageSize = CHARTER_CALENDAR_PAGE_SIZE

const tableMessages = defineMessages({
  showMoreDates: {
    id: 'LIfL6oXSbJk-SgAW57Pm',
    defaultMessage: 'Show more dates',
    description: 'Button text to load more departure dates in Maldives charter calendar',
  },
  datesColumnHeader: {
    id: '_xUbcl5o3K7tAuu56VGs',
    defaultMessage: 'Travel Dates',
    description: 'Sticky first-column header label on the Maldives charter desktop table calendar (Layout B); date rows below show Melbourne departure and Malé return lines.',
  },
  societeRewardsSubline: {
    id: 'NNc7TL-pXkXSdCbXiRGB',
    defaultMessage: '{loyaltyProgramName} Rewards',
    description: 'Second line of the Maldives charter desktop trip-options table sticky header for the loyalty-program reward fare columns; shown in brand green beside the rewards icon directly under the black cabin class name (Economy, Economy Plus, or Business). loyaltyProgramName is the configured loyalty program (e.g. Société).',
  },
  loading: {
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  },
  unavailable: {
    id: '2-yaAKUHBq54tPiTm42n',
    defaultMessage: 'Sold out!',
    description: 'Button label when charter flight date is unavailable',
  },
  noSeatsAvailable: {
    id: 'iv47cobHGcSpUSH6kWDd',
    defaultMessage: 'Sold out!',
    description: 'Short label in the Maldives charter flight calendar when a fare column has no reward-seat inventory or no seats for that cabin.',
  },
  notEnoughSeats: {
    id: 'n09IJ063vIxfUT_FhQPp',
    defaultMessage: 'Sold out!',
    description: 'Maldives charter desktop table calendar (Layout C) Société Rewards cell label when the leg has reward-seat inventory but fewer remaining seats than the requested seat-eligible passenger count.',
  },
  departMelbourne: {
    id: 'HroFmiqMFBQBLiNajy1i',
    defaultMessage: 'Depart Melbourne',
    description: 'Muted label above the formatted departure date in the sticky first column of the Maldives charter desktop table calendar (Layout B); identifies the outbound leg city.',
  },
  departMale: {
    id: 'wo7zJ4aJVCDNXUU_6wtT',
    defaultMessage: 'Depart Malé',
    description: 'Muted label above the formatted return departure date in the sticky first column of the Maldives charter desktop table calendar (Layout B); identifies the inbound leg city.',
  },
  emptyHeading: {
    id: 'guyDFMVuGdGAJHuZXY-E',
    defaultMessage: 'No departures available for {selectedMonth}',
    description: 'Empty state heading when no charter departures found for selected month',
  },
  emptyBody: {
    id: '2y3hsx4QLvrR_aamxC4j',
    defaultMessage: 'Try selecting a different month or adjusting your search criteria',
    description: 'Help text when no charter departures are available for selected month',
  },
  rewardOnlyEmptyHeading: {
    id: 'e8Rp2kXtWCQV6l_QWs0-',
    defaultMessage: 'No reward seat departures available for {selectedMonth}',
    description: 'Empty state heading on the Maldives charter desktop trip-options table when the "Reward seats" toggle hides every date in the selected month.',
  },
  rewardOnlyEmptyBody: {
    id: 'zFmM_B5jFTj1kIADqaFW',
    defaultMessage: 'Turn off the Reward seats filter to see all available departures for this month, or try selecting a different month.',
    description: 'Help text on the Maldives charter desktop trip-options table reward-only empty state, prompting the customer to disable the toggle or pick another month.',
  },
  infiniteOnlyEmptyHeading: {
    id: 'Q1_UWWNiqgqJqzkKyMvy',
    defaultMessage: 'No Infinite Member Price departures available for {selectedMonth}',
    description: 'Empty state heading on the Maldives charter desktop trip-options table when the "Only show Infinite Member Price" filter hides every date in the selected month.',
  },
  infiniteOnlyEmptyBody: {
    id: 'epXTTMNBXOcW6v7o1RXE',
    defaultMessage: 'Turn off the Infinite Member Price filter to see all available departures for this month, or try selecting a different month.',
    description: 'Help text on the Maldives charter desktop trip-options table Infinite-only empty state, prompting the customer to disable the filter or pick another month.',
  },
  selectFare: {
    id: 'WcCEmvRgX3nnalxltSsx',
    defaultMessage: 'Select',
    description: 'Maldives charter desktop trip-options table expanded row per-cabin button to commit the selected fare class for the row.',
  },
  selectedFare: {
    id: 'vLDtsTIXwXWp8yXLCilU',
    defaultMessage: 'Selected',
    description: 'Maldives charter desktop trip-options table expanded row per-cabin button label when this cabin is the active selection on the row.',
  },
  fromLabel: {
    id: '61Gm0owvNXkEHBOdcx8J',
    defaultMessage: 'From',
    description: 'Price prefix label in Maldives flight calendar item',
  },
  totalSuffix: {
    id: 'j--PuUYLTgWUxAxROh0Z',
    defaultMessage: 'total',
    description: 'Total pricing unit suffix in the Maldives calendar',
  },
  scrollLeftAriaLabel: {
    id: '7_u_j89wUh_XE9IAaX1V',
    defaultMessage: 'Scroll fare class columns left',
    description: 'Accessible label for the left scroll arrow on the Maldives charter desktop trip-options table.',
  },
  scrollRightAriaLabel: {
    id: '9G6Uey8gzxPgzG0VlKzT',
    defaultMessage: 'Scroll fare class columns right',
    description: 'Accessible label for the right scroll arrow on the Maldives charter desktop trip-options table.',
  },
  perPersonSuffix: {
    id: 'ew2ARy7ro3tFSM4inpHN',
    defaultMessage: '/person',
    description: 'Per person pricing unit suffix in Maldives calendar',
  },
  showDetails: {
    id: '-zlQ-vbqshTURzY2731T',
    defaultMessage: 'Show details',
    description: 'Toggle text in the Maldives charter desktop trip-options table to expand the fare details for a row.',
  },
  hideDetails: {
    id: 'tKRkoGVQuVogBZ3TIH5M',
    defaultMessage: 'Hide details',
    description: 'Toggle text in the Maldives charter desktop trip-options table to collapse the expanded fare details for a row.',
  },
})

/** Scroll slightly more than half a viewport so users keep table context between jumps. */
const TABLE_SCROLL_PAGE_FRACTION = 0.6

const TableWrap = styled.div`
  position: relative;
  width: 100%; min-width: 0;
  max-width: ${rem(1440)};
  margin: 0 auto;
`
const HorizontalScrollTrack = styled.div`
  width: 100%; min-width: 0;
  overflow-x: auto; overflow-y: clip; overscroll-behavior-x: contain;
`
type ScrollArrowButtonBaseProps = Omit<ComponentProps<typeof IconButton>, 'kind' | 'variant' | 'shape' | 'type'>

function ScrollArrowButtonBase(props: ScrollArrowButtonBaseProps) {
  return (
    <IconButton
      {...props}
      kind="secondary"
      variant="default"
      shape="circle"
      type="button"
    />
  )
}

/** Circular LuxKit scroll arrow button overlaid on the table edges. */
const ScrollArrowButton = styled(ScrollArrowButtonBase)`
  box-shadow: ${p => p.theme.shadow.bottom.small};
`
/** Sticky overlay that pins the column-header scroll arrows to the top of the modal viewport while
 *  the customer scrolls vertically. `height: 0` keeps the overlay out of the table's layout flow;
 *  child arrow slots are positioned absolutely. */
const ScrollArrowOverlay = styled.div`
  position: sticky;
  top: 0;
  height: 0;
  z-index: ${zIndex.stickyPromptCard};
  pointer-events: none;
`
const ScrollArrowSlot = styled.div<{ $side: 'start' | 'end'; $hidden: boolean }>`
  position: absolute;
  top: ${rem(16)};
  ${p => p.$side === 'start' ? css`inset-inline-start: ${rem(-20)};` : css`inset-inline-end: ${rem(-20)};`}
  pointer-events: auto;
  transition: opacity 0.2s ease;
  ${p => p.$hidden && css`
    opacity: 0;
    pointer-events: none;
  `}
`
/** Absolute overlay anchored to the Y measured from the expanded-row marker — renders the second
 *  pair of horizontal scroll arrows in {@link TableWrap} (outside the horizontal scroll track) so
 *  the buttons can use the same negative `left/right` overhang as the column-header pair without
 *  being clipped by the scroll container's `overflow-x: auto`.
 *
 *  Inner `ScrollArrowSlot` `top: 16` is overridden with a vertical translate so the arrow's centre
 *  lands on the measured anchor line (the boundary between the Baggage and Seats rows) rather than
 *  16px below it. */
const ExpandedSectionArrowOverlay = styled.div`
  position: absolute;
  inset-inline: 0;
  height: 0;
  z-index: ${zIndex.stickyHeader};
  pointer-events: none;

  ${ScrollArrowSlot} {
    top: 0;
    transform: translateY(-50%);
  }
`
const StickyHeaderOverlay = styled.div`
  position: sticky;
  top: -${rem(1)};
  height: 0;
  z-index: ${zIndex.stickySubHeader};
  pointer-events: none;
`
const StickyHeaderScrollTrack = styled.div`
  width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
`

const StyledTable = styled.table`
  table-layout: fixed; border-collapse: separate; width: 100%;
  border-spacing: 0;
`
const RowTableWrapper = styled.div`
  position: relative;
  background-color: ${p => p.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  border-radius: ${p => p.theme.borderRadius.M};
  overflow: hidden;

  ${StyledTable} {
    border-radius: inherit;
  }

  &.active {
    border-color: ${p => p.theme.palette.neutral.default.four};
    box-shadow: ${p => p.theme.shadow.flat.medium};
    z-index: ${zIndex.offerFilters};
  }
`
const HeaderTableWrapper = styled.div<{ $isPainted?: boolean }>`
  position: relative;
  box-shadow: ${p => p.theme.shadow.flat.medium};
  z-index: ${zIndex.offerFilters};
  ${p => !p.$isPainted && css`
    opacity: 0;
    pointer-events: none;
  `}
`
const HeaderThead = styled.thead`
  background-color: ${p => p.theme.palette.neutral.surface.normalBackground};
`

// Each row renders as a self-contained "card" — outermost cells own the side borders +
// rounded corners; middle cells share top + bottom borders. When a row is expanded, the
// date row's bottom corners flatten and the Baggage / Seats rows (rendered as further
// `<tr>`s in the same body) extend the same card downward.
const cellSurface = css`
  background-color: ${p => p.theme.palette.neutral.surface.normalBackground};
`
const StickyTh = styled.th`
  ${stickyDateColumn}
  z-index: ${zIndex.searchMenu};
  padding: ${rem(24)} ${rem(12)};
  vertical-align: middle;
  text-align: start;
  ${cellSurface}
  border-top: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  border-inline-start: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  border-start-start-radius: ${p => p.theme.borderRadius.S};
  border-end-start-radius: ${p => p.theme.borderRadius.S};
  ${verticalCellDivider}
`
const FareTh = styled.th<{ $isLastFareColumn: boolean }>`
  min-width: ${FARE_COL_MIN_WIDTH};
  padding: ${rem(6)} ${rem(6)};
  text-align: center; vertical-align: middle;
  ${cellSurface}
  border-top: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${p => p.theme.palette.neutral.surface.lightForeground};
  ${verticalCellDivider}
  ${p => p.$isLastFareColumn && css`
    border-inline-end: 1px solid ${p.theme.palette.neutral.surface.lightForeground};
    border-start-end-radius: ${p.theme.borderRadius.S};
    border-end-end-radius: ${p.theme.borderRadius.S};
  `}
`
const MainDateRowTr = styled.tr``
const CardTbody = styled.tbody``
const StickyDateTd = styled.td<{ $isExpanded: boolean }>`
  ${stickyDateColumn}
  z-index: ${zIndex.offerFilters};
  padding: ${rem(14)} ${rem(12)};
  height: ${p => p.$isExpanded ? rem(1) : 'auto'};
  ${cellSurface}
  border-start-start-radius: ${p => p.theme.borderRadius.S};
  ${p => p.$isExpanded
    ? css`
        background-color: ${p.theme.palette.neutral.surface.lightBackground};
        ${verticalCellDivider}
      `
    : css`border-end-start-radius: ${p.theme.borderRadius.S};`}
`
const FareTd = styled.td<{ $isLastFareColumn: boolean; $isExpanded: boolean }>`
  min-width: ${FARE_COL_MIN_WIDTH};
  position: relative;
  padding: 0;
  height: ${p => p.$isExpanded ? rem(1) : 'auto'};
  ${cellSurface}
  ${p => !p.$isLastFareColumn && p.$isExpanded && verticalCellDivider}
  vertical-align: ${p => p.$isExpanded ? 'top' : 'middle'};
  ${p => p.$isLastFareColumn && css`
    border-start-end-radius: ${p.theme.borderRadius.S};
    ${!p.$isExpanded && css`border-end-end-radius: ${p.theme.borderRadius.S};`}
  `}
`
const TileGapTd = styled.td`
  height: ${rem(24)}; padding: 0; border: none; line-height: 0; font-size: 0;
  background-color: ${p => p.theme.palette.neutral.surface.lightBackground};
`
/** Tappable wrapper for the sticky date cell — clicking anywhere on the date column toggles the expanded comparison row. */
const DateColumnToggleSurface = styled(Clickable)`
  display: block;
  width: 100%;
  text-align: start;
  border-radius: ${p => p.theme.borderRadius.S};
  cursor: pointer;
  &:focus-visible {
    ${focusStyle}
  }
`
/** Tappable wrapper for the price block when a row is expanded — retap collapses the comparison grid without nesting CTAs inside a single outer button. */
const ExpandedPriceTapSurface = styled(Clickable)`
  display: block;
  width: 100%;
  text-align: center;
  border-radius: ${p => p.theme.borderRadius.S};
  cursor: pointer;
  &:focus-visible {
    ${focusStyle}
  }
`

const RETURN_FARE_FREE_FLAME_SIZE = 18

const PriceWithReturnFareFreeRow = styled(Group)`
  max-width: 100%;
`
const ExpandedCellWrap = styled.div`
  display: grid;
  grid-template-rows: minmax(${rem(96)}, 1fr) ${rem(44)} auto;
  width: 100%;
  height: 100%;
  min-height: ${rem(156)};
  padding: ${rem(12)};
  gap: ${rem(12)};
`
const ExpandedActionArea = styled.div`
  width: 100%;
`
const DetailsActionArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`
const PriceSummaryArea = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const SoldOutSummaryArea = styled.div`
  grid-row: 1 / 3;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`
const CellSurface = styled(Clickable)<{ $disabled: boolean }>`
  display: block; width: 100%; min-height: ${rem(72)};
  padding: ${rem(12)}; text-align: center;
  border-radius: ${p => p.theme.borderRadius.S};
  cursor: pointer;
  ${p => p.$disabled && 'opacity: 0.5;'}
  &:focus-visible {
    ${focusStyle}
  }
`
const DetailsToggleLabel = styled(Caption)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: underline;
`
const DetailsToggleButton = styled(Clickable)`
  display: inline-flex;
  align-self: center;
  border-radius: ${p => p.theme.borderRadius.S};
  cursor: pointer;
  &:focus-visible {
    ${focusStyle}
  }
`

const LegArrow = styled.span`
  display: flex; flex-shrink: 0; align-items: center;
  color: ${p => p.theme.palette.neutral.surface.darkForeground};
`
const DayOffsetSup = styled.sup`
  margin-inline-start: ${rem(2)};
  font-size: 0.6em;
  vertical-align: super;
`

const ShowMoreContainer = styled.div`
  padding: ${rem(20)} ${rem(20)} 0;
  display: flex;
  justify-content: center;
`

const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

interface FareClassCellProps {
  fareClassColumn: FareClassWithReward
  checkInDate: string
  bundleData: CalendarItemBundleData
  /** Optional per-cabin hotel min for this column's cabin on this date. When present,
   *  overrides `bundleData.hotelPrice`/`hotelMemberPrice` so each column's hotel reflects
   *  the cabin's own qualifying-offer set, independent of the page-level cabin filter. */
  cabinHotelEntry?: MaldivesCabinHotelEntry
  seatEligiblePassengers: number
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isRowHighlighted: boolean
  isSelected: boolean
  isExpanded: boolean
  canShowDetails: boolean
  onHighlightRow: () => void
  onToggleExpand: () => void
  onSelectFare: (fareClass: FareClassWithReward) => void
  loadingLabel: string
  noSeatsAvailableLabel: string
  selectFareLabel: string
  selectedFareLabel: string
  showDetailsLabel: string
  hideDetailsLabel: string
  fromLabel: string
  priceSuffixLabel: string
  rewardCashAddOnLabel: (cashCoPay: number) => React.ReactNode
  loginToViewLabel: string
  enrollLabel: string
  isLoggedIn: boolean
  hasLoyaltyAccount: boolean
  isInfiniteMember: boolean
  onLoginClick: () => void
  onEnrollClick: () => void
}

const FareClassCell = memo(function FareClassCell(props: FareClassCellProps) {
  const {
    fareClassColumn, checkInDate, bundleData, cabinHotelEntry, seatEligiblePassengers, priceMode, luxPlusEnabled,
    isRowHighlighted, isSelected, isExpanded, canShowDetails, onHighlightRow, onToggleExpand, onSelectFare,
    loadingLabel, noSeatsAvailableLabel,
    selectFareLabel, selectedFareLabel, showDetailsLabel, hideDetailsLabel, fromLabel, priceSuffixLabel, rewardCashAddOnLabel,
    loginToViewLabel, enrollLabel, isLoggedIn, hasLoyaltyAccount, isInfiniteMember, onLoginClick, onEnrollClick,
  } = props
  const { getLegResult, getJourneysForCabinDate, isLoading: isRewardAvailabilityLoading } = useCharterRewardAvailabilityContext()
  const showHighlightedContent = isExpanded || isRowHighlighted
  const detailsToggleLabel = isExpanded ? hideDetailsLabel : showDetailsLabel
  const detailsToggleIcon = isExpanded ? <LineAngleUpIcon size="XS" /> : <LineAngleDownIcon size="XS" />
  const detailsToggle = canShowDetails ? (
    <DetailsToggleButton type="button" onClick={onToggleExpand}>
      <DetailsToggleLabel variant="medium" weight="bold" colour="neutral-two">
        {detailsToggleLabel}
        {detailsToggleIcon}
      </DetailsToggleLabel>
    </DetailsToggleButton>
  ) : null

  // Resolve cabin-specific journeys for this row+column from the per-cabin shell map.
  // `toBaseFareClass` strips the reward suffix so reward columns reuse their cabin's cash
  // journeys (the per-cabin shell fetch only covers cash fare-family).
  const cabinJourneys = getJourneysForCabinDate(toBaseFareClass(fareClassColumn), checkInDate)
  const departingFlight = cabinJourneys?.departing
  const returningFlight = cabinJourneys?.returning

  // Prefer the per-cabin hotel entry as the authoritative source so the column's hotel
  // (regular AND member price) reflects only offers that allow this cabin. We fall back
  // to the row-level bundleData ONLY when the per-cabin map hasn't been threaded through
  // at all — when the entry exists but its `hotelMemberPrice` happens to be undefined
  // (because at least one allocated package lacked a member rate), keep it undefined so
  // the cell renders the regular total rather than mixing in the page-cabin's member price.
  const cellHotelPrice = cabinHotelEntry ? cabinHotelEntry.hotelPrice : bundleData.hotelPrice
  const cellHotelMemberPrice = cabinHotelEntry ? cabinHotelEntry.hotelMemberPrice : bundleData.hotelMemberPrice

  const cellPrice = useMemo(() => computeFareClassCellPrice({
    fareClass: fareClassColumn,
    hotelPrice: cellHotelPrice,
    hotelMemberPrice: cellHotelMemberPrice,
    departingFlight,
    returningFlight,
    seatEligiblePassengers,
    getLegResult,
  }), [fareClassColumn, cellHotelPrice, cellHotelMemberPrice, departingFlight, returningFlight, seatEligiblePassengers, getLegResult])

  const isReward = isRewardFareClass(fareClassColumn)
  const showReturnFareFree = luxPlusEnabled && shouldShowReturnFareFreeCalendarLabel({
    hasReturnFareFree: hasReturnFareFreeHelper(returningFlight),
    isEconomy: toBaseFareClass(fareClassColumn) === FlightsClassTypes.ECONOMY,
  })
  const rewardLacksPoints = isReward && cellPrice.pointsTotal === undefined
  // Cash cell renders the member price when one is available; otherwise it falls back to the
  // regular price. The lux-plus colour applies whenever the LuxPlus toggle is on so the visual
  // indicator is consistent across cells even when member-price data isn't returned for a date.
  const showMemberCash = luxPlusEnabled && cellPrice.cashMemberTotal !== undefined
  const rawCashDisplay = showMemberCash ? cellPrice.cashMemberTotal : cellPrice.cashTotal
  const perPersonDivisor = priceMode === 'perPerson' && seatEligiblePassengers > 0 ? seatEligiblePassengers : 1
  const cashDisplayTotal = rawCashDisplay !== undefined ? rawCashDisplay / perPersonDivisor : undefined
  const isCellAvailable = bundleData.available && cashDisplayTotal !== undefined && !rewardLacksPoints

  const promotionSeatsLeftLabel = luxPlusEnabled && cellPrice.promotionSeatsLeft && cellPrice.promotionSeatsLeft > 0 ? (
    <Caption variant="medium" weight="bold" align="center" colour="urgency">
      <FormattedMessage
        id="23YWa0_2ZjY7SjWiY2Ak"
        defaultMessage="{count, plural, one {# seat left at this price} other {# seats left at this price}}"
        description="Urgency message showing limited seats remaining for charter flight"
        values={{ count: cellPrice.promotionSeatsLeft }}
      />
    </Caption>
  ) : null

  const rewardLockedForLogin = isReward && !isLoggedIn
  const rewardLockedForEnroll = isReward && isLoggedIn && !hasLoyaltyAccount
  const rewardLocked = rewardLockedForLogin || rewardLockedForEnroll
  const baseCabinHasSeats = cellPrice.cashTotal !== undefined

  let inner: React.ReactNode
  if (bundleData.fetching || (isReward && isRewardAvailabilityLoading)) {
    inner = <BodyText variant="small" colour="neutral-three">{loadingLabel}</BodyText>
  } else if (rewardLocked && !baseCabinHasSeats) {
    // Base cabin has no seats — reward can't be available either
    inner = <BodyText variant="small" colour="neutral-three">{noSeatsAvailableLabel}</BodyText>
  } else if (rewardLocked) {
    const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClassColumn)]
    const previewShowMember = luxPlusEnabled && cellHotelMemberPrice !== undefined
    const hotelBase = (previewShowMember ? cellHotelMemberPrice : cellHotelPrice) ?? 0
    const previewColour = previewShowMember ? 'lux-plus' as const : undefined
    const preview = rate && cellHotelPrice !== undefined && baseCabinHasSeats ? {
      points: (rate.pointsPerPerson * seatEligiblePassengers) / perPersonDivisor,
      cash: (hotelBase + rate.cashPerPerson * seatEligiblePassengers) / perPersonDivisor,
    } : undefined
    const previewPriceElement = preview ? (
      <Group direction="vertical" horizontalAlign="center" gap={2} fullWidth>
        {showHighlightedContent && <PriceRowPriceCaption>{fromLabel}</PriceRowPriceCaption>}
        <Heading as="div" variant="heading5" wrap="pretty" colour={previewColour}>
          <FormatLoyaltyPoints abbreviated points={preview.points} />
        </Heading>
        <Caption variant="medium" align="center" weight="normal" colour={previewColour}>
          {rewardCashAddOnLabel(preview.cash)}
        </Caption>
        {showHighlightedContent && (
          <>
            <Caption variant="large" align="center" weight="normal" colour={previewColour} format="lowercase">
              {priceSuffixLabel}
            </Caption>
            <PriceRowAgentHubCommission
              productType="flight"
              offerId={departingFlight?.id || returningFlight?.id || ''}
              offer={departingFlight || returningFlight ? { journey: (departingFlight || returningFlight) as App.JourneyV2 } : undefined}
              offerTotal={preview.cash}
            />
          </>
        )}
      </Group>
    ) : (
      <BodyText variant="small" colour="neutral-three">{loginToViewLabel}</BodyText>
    )
    if (showHighlightedContent) {
      return (
        <ExpandedCellWrap>
          <PriceSummaryArea>
            <ExpandedPriceTapSurface as="div">
              {previewPriceElement}
            </ExpandedPriceTapSurface>
          </PriceSummaryArea>
          <ExpandedActionArea>
            <TextButton kind="secondary" onClick={rewardLockedForEnroll ? onEnrollClick : onLoginClick} fit="flex">
              {rewardLockedForEnroll ? enrollLabel : loginToViewLabel}
            </TextButton>
          </ExpandedActionArea>
          <DetailsActionArea>{detailsToggle}</DetailsActionArea>
        </ExpandedCellWrap>
      )
    }
    inner = previewPriceElement
  } else if (!isCellAvailable || cashDisplayTotal === undefined) {
    const label = noSeatsAvailableLabel
    inner = (
      <BodyText variant="small" colour="neutral-three">
        {label}
      </BodyText>
    )
  } else if (isReward && cellPrice.pointsTotal !== undefined && cellPrice.cashTotal !== undefined) {
    const rewardCashColour = showMemberCash ? 'lux-plus' : undefined
    const rawRewardCash = showMemberCash && cellPrice.cashMemberTotal !== undefined ? cellPrice.cashMemberTotal : cellPrice.cashTotal
    const rewardCashDisplay = rawRewardCash / perPersonDivisor
    const rewardPointsDisplay = cellPrice.pointsTotal / perPersonDivisor
    inner = showHighlightedContent ? (
      <Group direction="vertical" horizontalAlign="center" gap={2} fullWidth>
        <PriceRowPriceCaption>{fromLabel}</PriceRowPriceCaption>
        <Heading as="div" variant="heading5" wrap="pretty" colour={rewardCashColour}>
          <FormatLoyaltyPoints abbreviated points={rewardPointsDisplay} />
        </Heading>
        <Caption variant="medium" align="center" weight="normal" colour={rewardCashColour}>
          {rewardCashAddOnLabel(rewardCashDisplay)}
        </Caption>
        <Caption variant="large" align="center" weight="normal" colour={rewardCashColour} format="lowercase">
          {priceSuffixLabel}
        </Caption>
        <PriceRowAgentHubCommission
          productType="flight"
          offerId={departingFlight?.id || returningFlight?.id || ''}
          offer={departingFlight || returningFlight ? { journey: (departingFlight || returningFlight) as App.JourneyV2 } : undefined}
          offerTotal={rewardCashDisplay}
        />
      </Group>
    ) : (
      <Group direction="vertical" horizontalAlign="center" gap={2} fullWidth>
        <Heading as="div" variant="heading5" wrap="pretty" colour={rewardCashColour}>
          <FormatLoyaltyPoints abbreviated points={rewardPointsDisplay} />
        </Heading>
        <Caption variant="medium" align="center" weight="normal" colour={rewardCashColour}>
          {rewardCashAddOnLabel(rewardCashDisplay)}
        </Caption>
      </Group>
    )
  } else {
    const cashColour = luxPlusEnabled ? 'lux-plus' : undefined
    const fnfLabel = cellPrice.isFnf
      ? <MaldivesFnfSpecialOfferLabel isInfiniteMember={isInfiniteMember} />
      : null
    inner = showHighlightedContent ? (
      <Group direction="vertical" horizontalAlign="center" gap={2} fullWidth>
        <PriceRowPriceCaption>{fromLabel}</PriceRowPriceCaption>
        <PriceWithReturnFareFreeRow direction="horizontal" horizontalAlign="center" verticalAlign="center" gap={2}>
          {showReturnFareFree && <SolidFireIcon size={RETURN_FARE_FREE_FLAME_SIZE} colour="lux-plus" aria-hidden />}
          <Heading as="div" variant="heading5" wrap="pretty" colour={cashColour}>
            <FormatCurrency value={cashDisplayTotal} format="roundedDollar" />
          </Heading>
        </PriceWithReturnFareFreeRow>
        <Caption variant="large" align="center" weight="normal" colour={cashColour ?? 'neutral-two'} format="lowercase">
          {priceSuffixLabel}
        </Caption>
        {fnfLabel}
        {promotionSeatsLeftLabel}
        <PriceRowAgentHubCommission
          productType="flight"
          offerId={departingFlight?.id || returningFlight?.id || ''}
          offer={departingFlight || returningFlight ? { journey: (departingFlight || returningFlight) as App.JourneyV2 } : undefined}
          offerTotal={cashDisplayTotal}
        />
      </Group>
    ) : (
      <Group direction="vertical" horizontalAlign="center" gap={2}>
        <PriceWithReturnFareFreeRow direction="horizontal" horizontalAlign="center" verticalAlign="center" gap={2}>
          {showReturnFareFree && <SolidFireIcon size={RETURN_FARE_FREE_FLAME_SIZE} colour="lux-plus" aria-hidden />}
          <Heading as="div" variant="heading5" wrap="pretty" colour={cashColour}>
            <FormatCurrency value={cashDisplayTotal} format="roundedDollar" />
          </Heading>
        </PriceWithReturnFareFreeRow>
        {fnfLabel}
        {promotionSeatsLeftLabel}
      </Group>
    )
  }

  const interactionDisabled = (!isCellAvailable && !rewardLocked) || (rewardLocked && !baseCabinHasSeats)

  if (showHighlightedContent) {
    let fareActionButton: React.ReactNode = null
    if (!interactionDisabled) {
      if (isSelected) {
        fareActionButton = (
          <TextButton kind="primary" nonInteractable tabIndex={-1} fit="flex">
            {selectedFareLabel}
          </TextButton>
        )
      } else {
        fareActionButton = (
          <TextButton kind="secondary" onClick={() => onSelectFare(fareClassColumn)} fit="flex">
            {selectFareLabel}
          </TextButton>
        )
      }
    }
    const priceSummary = (
      <ExpandedPriceTapSurface as="div">
        {inner}
      </ExpandedPriceTapSurface>
    )
    return (
      <ExpandedCellWrap>
        {fareActionButton ? (
          <PriceSummaryArea>{priceSummary}</PriceSummaryArea>
        ) : (
          <SoldOutSummaryArea>{priceSummary}</SoldOutSummaryArea>
        )}
        {fareActionButton && <ExpandedActionArea>{fareActionButton}</ExpandedActionArea>}
        <DetailsActionArea>{detailsToggle}</DetailsActionArea>
      </ExpandedCellWrap>
    )
  }

  return (
    <CellSurface
      type="button"
      $disabled={interactionDisabled}
      onClick={onHighlightRow}
      aria-pressed={isSelected || undefined}
      aria-expanded={isExpanded}
    >
      {inner}
    </CellSurface>
  )
})

interface TableRowProps {
  item: DateItem
  nights: number
  rooms: Array<App.Occupants>
  selectedOptionId?: string
  isHighlighted: boolean
  onOptionSelect: (option: TripOption) => void
  fareClassColumns: ReadonlyArray<FareClassWithReward>
  fareColumnCellStyle: CSSProperties
  pageFareClass: FareClassWithReward
  seatEligiblePassengers: number
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  loadingLabel: string
  noSeatsAvailableLabel: string
  rewardCashAddOnLabel: (cashCoPay: number) => React.ReactNode
  dayOffsetText: string
  departMelbourneLabel: string
  departMaleLabel: string
  prefetchedBundleData?: CalendarItemBundleData
  isExpanded: boolean
  onHighlight: (id: string) => void
  onToggleExpand: (id: string) => void
  selectFareLabel: string
  selectedFareLabel: string
  showDetailsLabel: string
  hideDetailsLabel: string
  fromLabel: string
  priceSuffixLabel: string
  /** Ref callback forwarded into the expanded row so the parent table can measure the Y
   *  position of the Baggage / Seats boundary and render the second pair of scroll arrows
   *  in `TableWrap` (outside the horizontal scroll track) aligned with that level. */
  expandedAnchorRef?: React.RefCallback<HTMLSpanElement>
  loginToViewLabel: string
  enrollLabel: string
  isLoggedIn: boolean
  hasLoyaltyAccount: boolean
  isInfiniteMember: boolean
  onLoginClick: () => void
  onEnrollClick: () => void
  /** Per-cabin lowest hotel for this row's date. Each cell looks up its own cabin. */
  hotelByCabinForRow?: Partial<Record<FlightsClassTypes, MaldivesCabinHotelEntry>>
  feederOriginAirportCode?: string
}

const TableRow = memo(function TableRow(props: TableRowProps) {
  const {
    item, nights, rooms, selectedOptionId, onOptionSelect, fareClassColumns, fareColumnCellStyle, pageFareClass,
    isHighlighted, seatEligiblePassengers, luxPlusEnabled, loadingLabel, noSeatsAvailableLabel, rewardCashAddOnLabel,
    dayOffsetText, departMelbourneLabel, departMaleLabel, prefetchedBundleData,
    isExpanded, onHighlight, onToggleExpand, selectFareLabel, selectedFareLabel,
    showDetailsLabel, hideDetailsLabel, fromLabel, priceSuffixLabel, expandedAnchorRef,
    loginToViewLabel, enrollLabel, isLoggedIn, hasLoyaltyAccount, isInfiniteMember, onLoginClick, onEnrollClick,
    hotelByCabinForRow, feederOriginAirportCode,
  } = props
  const intl = useIntl()
  const handleHighlight = useCallback(() => onHighlight(item.id), [onHighlight, item.id])
  const handleToggleExpand = useCallback(() => onToggleExpand(item.id), [onToggleExpand, item.id])

  const fetchedBundleData = useCalendarItemData({
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    nights,
    rooms,
    fareClass: FlightsClassTypes.ECONOMY,
    priceMode: 'total',
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    feederOriginAirportCode,
    disabled: !!prefetchedBundleData,
  })
  const bundleData = prefetchedBundleData ?? fetchedBundleData
  const { getLegResult, getJourneysForCabinDate } = useCharterRewardAvailabilityContext()

  // Compute which columns are unavailable so the expanded row shows placeholder messages.
  const unavailableColumns = useMemo(() => {
    const set = new Set<FareClassWithReward>()
    for (const fc of fareClassColumns) {
      const cabinJourneys = getJourneysForCabinDate(toBaseFareClass(fc), item.checkIn)
      const cabinHotelEntry = hotelByCabinForRow?.[toBaseFareClass(fc)]
      const cellPrice = computeFareClassCellPrice({
        fareClass: fc,
        hotelPrice: cabinHotelEntry ? cabinHotelEntry.hotelPrice : bundleData.hotelPrice,
        hotelMemberPrice: cabinHotelEntry ? cabinHotelEntry.hotelMemberPrice : bundleData.hotelMemberPrice,
        departingFlight: cabinJourneys?.departing,
        returningFlight: cabinJourneys?.returning,
        seatEligiblePassengers,
        getLegResult,
      })
      const isReward = isRewardFareClass(fc)
      if (isReward && cellPrice.cashTotal === undefined) {
        // Base cabin has no seats — reward can't be available
        set.add(fc)
      } else if (isReward && !hasLoyaltyAccount && cellPrice.pointsTotal === undefined) {
        // Non-members can't check reward availability — but base cabin has seats (cell shows preview)
        // Don't mark as unavailable so baggage/earn rows still render
      } else if (isReward && cellPrice.pointsTotal === undefined) {
        set.add(fc)
      } else if (!isReward && (cellPrice.cashTotal === undefined || !bundleData.available)) {
        set.add(fc)
      }
    }
    return set
  }, [fareClassColumns, getJourneysForCabinDate, item.checkIn, bundleData, seatEligiblePassengers, getLegResult, hasLoyaltyAccount, hotelByCabinForRow])

  // Per-cell flight journeys are resolved inside <FareClassCell> via the cabin-keyed
  // context populated by the shell, so the per-row Economy-only `useFlightItemData` call
  // is no longer needed here.

  const isRowSelected = item.id === selectedOptionId
  const isRowHighlighted = isExpanded || isRowSelected || isHighlighted
  const canShowDetails = unavailableColumns.size < fareClassColumns.length
  const lastFareColumnIndex = fareClassColumns.length - 1

  const handleCellSelect = useCallback((selectedCellFareClass: FareClassWithReward) => {
    onOptionSelect({
      id: item.id,
      departDate: item.departDate,
      returnDate: item.returnDate,
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      nights,
      price: bundleData.totalPrice,
      memberPrice: bundleData.memberTotalPrice,
      currency: '',
      seatsLeft: bundleData.seatsLeft,
      available: true,
      fareClass: selectedCellFareClass,
    })
  }, [item, nights, bundleData.totalPrice, bundleData.memberTotalPrice, bundleData.seatsLeft, onOptionSelect])

  const dateColumnContent = (
    <Group direction="horizontal" verticalAlign="center" gap={8} fullWidth>
      <Group direction="vertical" gap={2} grow>
        <BodyText variant="small" colour="neutral-three">{departMelbourneLabel}</BodyText>
        <BodyText variant="medium" weight="bold" wrap="no-wrap">
          {intlDateFormatter(item.departDate, INTL_DAY_NAME_DAY_MONTH, intl.locale)}
        </BodyText>
      </Group>
      <LegArrow aria-hidden><LineArrowRightIcon size="S" /></LegArrow>
      <Group direction="vertical" gap={2} grow>
        <BodyText variant="small" colour="neutral-three">{departMaleLabel}</BodyText>
        <BodyText variant="medium" weight="bold" wrap="no-wrap">
          {intlDateFormatter(item.checkOut, INTL_DAY_NAME_DAY_MONTH, intl.locale)}
          <DayOffsetSup>{dayOffsetText}</DayOffsetSup>
        </BodyText>
      </Group>
    </Group>
  )

  return (
    <>
      <MainDateRowTr>
        <StickyDateTd $isExpanded={isRowHighlighted}>
          <DateColumnToggleSurface type="button" onClick={handleHighlight} aria-expanded={isExpanded}>
            {dateColumnContent}
          </DateColumnToggleSurface>
        </StickyDateTd>
        {fareClassColumns.map((fareClassColumn, idx) => (
          <FareTd
            key={fareClassColumn}
            data-testid={`maldives-charter-${fareClassColumn}-fare`}
            style={fareColumnCellStyle}
            $isLastFareColumn={idx === lastFareColumnIndex}
            $isExpanded={isRowHighlighted}
          >
            <FareClassCell
              fareClassColumn={fareClassColumn}
              checkInDate={item.checkIn}
              bundleData={bundleData}
              cabinHotelEntry={hotelByCabinForRow?.[toBaseFareClass(fareClassColumn)]}
              seatEligiblePassengers={seatEligiblePassengers}
              priceMode={props.priceMode}
              luxPlusEnabled={luxPlusEnabled}
              isRowHighlighted={isRowHighlighted}
              isSelected={isRowSelected && fareClassColumn === pageFareClass}
              isExpanded={isExpanded}
              canShowDetails={canShowDetails}
              onHighlightRow={handleHighlight}
              onToggleExpand={handleToggleExpand}
              onSelectFare={handleCellSelect}
              loadingLabel={loadingLabel}
              noSeatsAvailableLabel={noSeatsAvailableLabel}
              selectFareLabel={selectFareLabel}
              selectedFareLabel={selectedFareLabel}
              showDetailsLabel={showDetailsLabel}
              hideDetailsLabel={hideDetailsLabel}
              fromLabel={fromLabel}
              priceSuffixLabel={priceSuffixLabel}
              rewardCashAddOnLabel={rewardCashAddOnLabel}
              loginToViewLabel={loginToViewLabel}
              enrollLabel={enrollLabel}
              isLoggedIn={isLoggedIn}
              hasLoyaltyAccount={hasLoyaltyAccount}
              isInfiniteMember={isInfiniteMember}
              onLoginClick={onLoginClick}
              onEnrollClick={onEnrollClick}
            />
          </FareTd>
        ))}
      </MainDateRowTr>
      {isExpanded && (
        <MaldivesCharterTripOptionsTableExpandedRow
          fareClassColumns={fareClassColumns}
          fareColumnCellStyle={fareColumnCellStyle}
          checkIn={item.checkIn}
          checkOut={item.checkOut}
          rooms={rooms}
          hotelLoyaltyCalculationRequest={bundleData.hotelLoyaltyCalculationRequest}
          expandedAnchorRef={expandedAnchorRef}
          unavailableColumns={unavailableColumns}
          feederOriginAirportCode={feederOriginAirportCode}
        />
      )}
    </>
  )
})

const MaldivesCharterTripOptionsTable = memo(function MaldivesCharterTripOptionsTable(props: Props) {
  const {
    currentMonthTripOptions, selectedMonth, dateRangesByMonth, luxPlusEnabled,
    selectedOptionId, onOptionSelect, nights, rooms, fareClass, rewardSeatsOnly = false, infiniteMemberOnly = false, cabinFilter,
    feederOriginAirportCode,
  } = props
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isLoggedIn = !!memberId
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const showModal = useContext(ModalContext)
  const handleLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const activateCta = intl.formatMessage({
    id: 'T9UHtgcdpQXkdUjc8gMK',
    defaultMessage: 'Activate membership',
    description: 'Primary button text on the loyalty join modal when a user clicks Join Société from the Maldives charter calendar.',
  })
  const handleEnrollClick = useCallback(async() => {
    const joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
      onAfterClose={() => {}}
      primaryActionCta={activateCta}
    />)
    if (joinConfirmed) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [showModal, activateCta, dispatch])
  const { isAvailableForDate, isLoading: isRewardAvailabilityLoading } = useCharterRewardAvailabilityContext()
  const [page, setPage] = useState<number>(1)
  const [expandedDateId, setExpandedDateId] = useState<string | undefined>()
  const [highlightedDateId, setHighlightedDateId] = useState<string | undefined>()
  const lastTrackedEmptyStateMonthRef = useRef<string | undefined>(undefined)
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const handleHighlight = useCallback((id: string) => {
    setHighlightedDateId(id)
  }, [])
  const handleToggleExpand = useCallback((id: string) => {
    setHighlightedDateId(id)
    setExpandedDateId(prev => (prev === id ? undefined : id))
  }, [])

  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )
  const fareClassColumns = useMemo<Array<FareClassWithReward>>(() => {
    const base: Array<FareClassWithReward> = isRewardSeatsEnabled
      ? expandMaldivesCharterFareClassesWithRewards(selectableCashCabins)
      : [...selectableCashCabins]
    // When the "Société Rewards" filter is on, drop cash cabins so the
    // table only shows the reward variants the customer is filtering for.
    const afterRewardFilter = rewardSeatsOnly ? base.filter(isRewardFareClass) : base
    // When a specific cabin is selected in the modal's class dropdown, keep only that base
    // cabin's columns (its cash variant plus, when applicable, its reward variant).
    return cabinFilter
      ? afterRewardFilter.filter(fc => toBaseFareClass(fc) === cabinFilter)
      : afterRewardFilter
  }, [isRewardSeatsEnabled, rewardSeatsOnly, cabinFilter, selectableCashCabins])

  const selectedMonthDateRanges = dateRangesByMonth[selectedMonth]
  const selectedMonthFirstDate = selectedMonthDateRanges?.[0]?.checkIn
  const selectedMonthMoment = selectedMonthFirstDate
    ? moment(selectedMonthFirstDate).startOf('month')
    : null
  const selectedMonthLabel = selectedMonthMoment ? intlDateFormatter(selectedMonthMoment.toDate(), INTL_SHORT_MONTH, intl.locale) : selectedMonth

  // Track whether the user has manually changed the month via the carousel.
  // Until they do, filter from the pre-selected date's month so earlier rows don't appear.
  const userChangedMonthRef = useRef(false)
  const prevSelectedMonthForFilterRef = useRef(selectedMonth)
  if (prevSelectedMonthForFilterRef.current !== selectedMonth) {
    userChangedMonthRef.current = true
    prevSelectedMonthForFilterRef.current = selectedMonth
  }

  const effectiveFilterMonth = useMemo(() => {
    if (!userChangedMonthRef.current && selectedOptionId) {
      const selectedItem = currentMonthTripOptions.find(i => i.id === selectedOptionId)
      if (selectedItem) {
        const itemMonth = moment(selectedItem.checkIn).startOf('month')
        if (selectedMonthMoment && itemMonth.isAfter(selectedMonthMoment)) {
          return itemMonth
        }
      }
    }
    return selectedMonthMoment
  }, [selectedOptionId, currentMonthTripOptions, selectedMonthMoment])

  const { getLegResult: getRewardLegResult, getJourneysForCabinDate: getRewardJourneys } = useCharterRewardAvailabilityContext()

  const visibleItems = useMemo(() => {
    return currentMonthTripOptions.filter((item) => {
      if (!effectiveFilterMonth) return false
      if (!moment(item.checkIn).startOf('month').isSameOrAfter(effectiveFilterMonth, 'month')) {
        return false
      }
      const bundleData = props.itemBundleDataByDate?.[item.checkIn]
      // Avoid hiding dates while hotel or flight data is still resolving. Once loaded, omit
      // dates where no cabin can form a bookable hotel + return-flight bundle.
      if (bundleData && !bundleData.fetching && !dateHasAvailableCashFare({
        checkIn: item.checkIn,
        cashCabins: selectableCashCabins,
        getJourneysForCabinDate: getRewardJourneys,
        hotelPriceByCabinAndDate: props.hotelPriceByCabinAndDate,
        fallbackHotelPrice: bundleData?.hotelPrice,
        seatEligiblePassengers,
        getLegResult: getRewardLegResult,
      })) {
        return false
      }
      if (rewardSeatsOnly && hasLoyaltyAccount) {
        if (isRewardAvailabilityLoading) return true
        // Only show the row if at least one reward cabin has sufficient seats.
        // Skip this check for non-members because live reward availability is not queried for them;
        // they see preview pricing with a login/join CTA in each reward cell.
        const hasAnyRewardAvailability = selectableCashCabins.some((cabin) => {
          const journeys = getRewardJourneys(cabin, item.checkIn)
          if (!journeys?.departing?.journeyKey || !journeys?.returning?.journeyKey) return false
          const depLeg = getRewardLegResult(journeys.departing.journeyKey)
          const retLeg = getRewardLegResult(journeys.returning.journeyKey)
          if (!depLeg?.available || !retLeg?.available) return false
          const depInv = depLeg.remainingInventory ?? 0
          const retInv = retLeg.remainingInventory ?? 0
          return Math.min(depInv, retInv) >= seatEligiblePassengers
        })
        if (!hasAnyRewardAvailability) return false
      }
      // Match the visible per-cabin badge: keep the row if any cash cabin has a bookable Infinite
      // Member (FnF) price (dateHasInfiniteMemberPrice), using the same per-cabin journey + hotel
      // data the cells use — not the cheapest-bundle aggregate `isFnfPriceApplied`. Skip while the
      // bundle pricing is still fetching so the row doesn't disappear before data resolves.
      if (infiniteMemberOnly && !props.itemBundleDataByDate?.[item.checkIn]?.fetching) {
        const hasInfiniteMemberPrice = dateHasInfiniteMemberPrice({
          checkIn: item.checkIn,
          cashCabins: selectableCashCabins,
          getJourneysForCabinDate: getRewardJourneys,
          hotelPriceByCabinAndDate: props.hotelPriceByCabinAndDate,
          seatEligiblePassengers,
          getLegResult: getRewardLegResult,
        })
        if (!hasInfiniteMemberPrice) {
          return false
        }
      }
      return true
    })
  }, [currentMonthTripOptions, effectiveFilterMonth, rewardSeatsOnly, infiniteMemberOnly, props.itemBundleDataByDate, props.hotelPriceByCabinAndDate, isRewardAvailabilityLoading, getRewardLegResult, getRewardJourneys, seatEligiblePassengers, hasLoyaltyAccount, selectableCashCabins])

  const limit = page * pageSize
  const itemsToShow = visibleItems.slice(0, limit)
  const hasMore = visibleItems.length > limit

  useEffect(() => {
    setPage(1)
    setExpandedDateId(undefined)
    setHighlightedDateId(undefined)
  }, [selectedMonth, selectedOptionId, dateRangesByMonth, currentMonthTripOptions, rewardSeatsOnly, isAvailableForDate])

  const onShowMore = useCallback(() => { setPage(prevPage => prevPage + 1) }, [])

  const horizontalScrollRef = useRef<HTMLDivElement>(null)
  const stickyHeaderScrollRef = useRef<HTMLDivElement>(null)
  const tableTextDirection = useTextDirection(horizontalScrollRef)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState<boolean>()
  const measureHorizontalOverflow = useCallback(() => {
    const element = horizontalScrollRef.current
    if (!element) return
    setHasHorizontalOverflow(element.scrollWidth > element.clientWidth)
  }, [])
  useResizeObserver(horizontalScrollRef, measureHorizontalOverflow)
  const syncStickyHeaderScroll = useCallback(() => {
    const source = horizontalScrollRef.current
    const target = stickyHeaderScrollRef.current
    if (!source || !target) return
    const sourceScrollLeft = getLogicalScrollLeft(source)
    if (getLogicalScrollLeft(target) === sourceScrollLeft) return
    setLogicalScrollLeft(target, sourceScrollLeft)
  }, [])
  const { hasReachedHorizontalStart, hasReachedHorizontalEnd } = useScrollEndsObserver(
    horizontalScrollRef,
    fareClassColumns.length,
    { initiallyHasReachedHorizontalStart: true, initiallyHasReachedHorizontalEnd: true },
  )
  const horizontalScrollPosition: ScrollPosition = hasReachedHorizontalStart ? 'start' : hasReachedHorizontalEnd ? 'end' : 'middle'
  const isScrollArrowHidden = useCallback((direction: PhysicalHorizontalDirection) => (
    isTripOptionsTableScrollArrowHidden(
      hasHorizontalOverflow,
      horizontalScrollPosition,
      tableTextDirection,
      direction,
    )
  ), [hasHorizontalOverflow, horizontalScrollPosition, tableTextDirection])

  const scrollByPhysicalDirection = useCallback((direction: PhysicalHorizontalDirection) => {
    const el = horizontalScrollRef.current
    if (!el) return
    scrollByLogicalPosition(el, {
      left: getLogicalScrollDeltaForPhysicalHorizontalDirection(tableTextDirection, direction, el.clientWidth * TABLE_SCROLL_PAGE_FRACTION),
      behavior: 'smooth',
    })
  }, [tableTextDirection])
  const handleScrollLeft = useCallback(() => {
    scrollByPhysicalDirection('left')
  }, [scrollByPhysicalDirection])
  const handleScrollRight = useCallback(() => {
    scrollByPhysicalDirection('right')
  }, [scrollByPhysicalDirection])

  const fareColCount = fareClassColumns.length

  const fareColWidthStyle = useMemo((): CSSProperties => {
    if (fareColCount === 0) {
      return { minWidth: FARE_COL_MIN_WIDTH }
    }
    const width = `calc((100% - ${STICKY_DATE_COL_WIDTH}) / ${fareColCount})`
    return { minWidth: FARE_COL_MIN_WIDTH, width }
  }, [fareColCount])

  // Table sizing: with many columns, enforce per-column minimums so horizontal scroll
  // kicks in. With fewer columns, cap at the month tile carousel width (~684px) and center.
  const MONTH_CAROUSEL_WIDTH = rem(684)
  const perColumnMin = `calc(${STICKY_DATE_COL_WIDTH} + ${fareColCount} * ${FARE_COL_MIN_WIDTH})`
  const needsScroll = fareColCount > 3
  const tableMinInlineWidth = useMemo(
    () => needsScroll ? perColumnMin : `max(${perColumnMin}, ${MONTH_CAROUSEL_WIDTH})`,
    [perColumnMin, needsScroll, MONTH_CAROUSEL_WIDTH],
  )
  const tableMaxInlineWidth = needsScroll ? undefined : MONTH_CAROUSEL_WIDTH
  useLayoutEffect(syncStickyHeaderScroll, [syncStickyHeaderScroll, fareColCount])
  useLayoutEffect(measureHorizontalOverflow, [measureHorizontalOverflow, fareColCount])

  const showMoreDatesText = intl.formatMessage(tableMessages.showMoreDates)
  const datesColumnHeader = intl.formatMessage(tableMessages.datesColumnHeader)
  const societeRewardsTableHeaderSubline = intl.formatMessage(tableMessages.societeRewardsSubline, { loyaltyProgramName: getLoyaltyProgramDisplayName() })
  const loadingLabel = intl.formatMessage(tableMessages.loading)
  const noSeatsAvailableLabel = intl.formatMessage(tableMessages.noSeatsAvailable)
  const loginToViewLabel = intl.formatMessage({
    id: 'kx9HisAhmWTyXzvb0xem',
    defaultMessage: 'Log in to view',
    description: 'Label shown when a logged-out user has a reward fare class selected; they must log in to see reward pricing.',
  })
  const enrollLabel = intl.formatMessage({
    id: 'YoeDFN4oz38auq910Hq0',
    defaultMessage: 'Join {loyaltyProgramName}',
    description: 'Button label shown when a logged-in user without a Société membership views a reward fare cell in the trip options calendar table.',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })
  const departMelbourneLabel = intl.formatMessage(tableMessages.departMelbourne)
  const departMaleLabel = intl.formatMessage(tableMessages.departMale)
  const dayOffsetText = intl.formatNumber(1, { signDisplay: 'always' })
  const selectFareLabel = intl.formatMessage(tableMessages.selectFare)
  const selectedFareLabel = intl.formatMessage(tableMessages.selectedFare)
  const showDetailsLabel = intl.formatMessage(tableMessages.showDetails)
  const hideDetailsLabel = intl.formatMessage(tableMessages.hideDetails)
  const fromLabel = intl.formatMessage(tableMessages.fromLabel)
  const scrollLeftAriaLabel = intl.formatMessage(tableMessages.scrollLeftAriaLabel)
  const scrollRightAriaLabel = intl.formatMessage(tableMessages.scrollRightAriaLabel)

  const tableWrapRef = useRef<HTMLDivElement>(null)
  const expandedAnchorElementRef = useRef<HTMLSpanElement | null>(null)
  const [expandedAnchorY, setExpandedAnchorY] = useState<number | null>(null)
  /** Stores the marker DOM node — actual Y measurement happens in the layout effect below so it
   *  fires after the table's row layout has settled (esp. once the Société earn cells finish
   *  fetching their points calculations and shift the row heights). */
  const expandedAnchorRef = useCallback((node: HTMLSpanElement | null) => {
    expandedAnchorElementRef.current = node
  }, [])
  useLayoutEffect(() => {
    const measure = () => {
      if (!expandedAnchorElementRef.current || !tableWrapRef.current) {
        setExpandedAnchorY(null)
        return
      }
      const wrapRect = tableWrapRef.current.getBoundingClientRect()
      const anchorRect = expandedAnchorElementRef.current.getBoundingClientRect()
      setExpandedAnchorY(anchorRect.top - wrapRect.top)
    }
    measure()
    if (!tableWrapRef.current) return
    /** Re-measure when row heights change (Société earn data loading shifts the layout). */
    const observer = new ResizeObserver(measure)
    observer.observe(tableWrapRef.current)
    return () => observer.disconnect()
  }, [expandedDateId])
  const priceSuffixLabel = intl.formatMessage(
    props.priceMode === 'perPerson' ? tableMessages.perPersonSuffix : tableMessages.totalSuffix,
  )

  const rewardCashAddOnLabel = useCallback(
    (cashCoPay: number) => (
      <FormattedMessage
        id="peHFjMVc2YKKQdYS2mU5"
        defaultMessage="+ {cash}"
        description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
        values={{ cash: <FormatCurrency value={cashCoPay} format="roundedDollar" /> }}
      />
    ),
    [],
  )

  const isEmptyState = currentMonthTripOptions.length === 0

  useEffect(() => {
    if (!isEmptyState) {
      lastTrackedEmptyStateMonthRef.current = undefined
      return
    }
    if (lastTrackedEmptyStateMonthRef.current === selectedMonth) return
    lastTrackedEmptyStateMonthRef.current = selectedMonth
    trackClientEvent({
      subject: 'no_departures_available_selected_month',
      action: 'impression',
      type: 'nonInteraction',
      category: 'maldives_direct_calendar',
    })
  }, [isEmptyState, selectedMonth])

  if (isEmptyState) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          {intl.formatMessage(tableMessages.emptyHeading, { selectedMonth: selectedMonthLabel })}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          {intl.formatMessage(tableMessages.emptyBody)}
        </BodyText>
      </EmptyStateGroup>
    )
  }

  if (rewardSeatsOnly && visibleItems.length === 0) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          {intl.formatMessage(tableMessages.rewardOnlyEmptyHeading, { selectedMonth: selectedMonthLabel })}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          {intl.formatMessage(tableMessages.rewardOnlyEmptyBody)}
        </BodyText>
      </EmptyStateGroup>
    )
  }

  if (infiniteMemberOnly && visibleItems.length === 0) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          {intl.formatMessage(tableMessages.infiniteOnlyEmptyHeading, { selectedMonth: selectedMonthLabel })}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          {intl.formatMessage(tableMessages.infiniteOnlyEmptyBody)}
        </BodyText>
      </EmptyStateGroup>
    )
  }

  const renderHeaderRow = () => (
    <tr>
      <StickyTh scope="col">
        <BodyText variant="small" weight="bold">{datesColumnHeader}</BodyText>
      </StickyTh>
      {fareClassColumns.map((fc, idx) => {
        const reward = isRewardFareClass(fc)
        const label = getCharterFareCabinLabel(intl, fc)
        let headerContent: React.ReactNode
        if (reward) {
          headerContent = (
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">{label}</BodyText>
              <Group direction="horizontal" verticalAlign="center" gap={8}>
                <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" />
                <BodyText variant="small" weight="bold" colour="highlight-primary-dark">
                  {societeRewardsTableHeaderSubline}
                </BodyText>
              </Group>
            </Group>
          )
        } else {
          headerContent = <BodyText variant="small" weight="bold" align="center">{label}</BodyText>
        }
        return (
          <FareTh
            key={fc}
            scope="col"
            style={fareColWidthStyle}
            $isLastFareColumn={idx === fareClassColumns.length - 1}
          >
            {headerContent}
          </FareTh>
        )
      })}
    </tr>
  )

  return (
    <TableWrap ref={tableWrapRef}>
      <ScrollArrowOverlay>
        <ScrollArrowSlot $side="start" $hidden={isScrollArrowHidden('left')}>
          <ScrollArrowButton onClick={handleScrollLeft} aria-label={scrollLeftAriaLabel}>
            <LineAngleLeftIcon rtlFlip={false} />
          </ScrollArrowButton>
        </ScrollArrowSlot>
        <ScrollArrowSlot $side="end" $hidden={isScrollArrowHidden('right')}>
          <ScrollArrowButton onClick={handleScrollRight} aria-label={scrollRightAriaLabel}>
            <LineAngleRightIcon rtlFlip={false} />
          </ScrollArrowButton>
        </ScrollArrowSlot>
      </ScrollArrowOverlay>
      {expandedAnchorY !== null && (
        <ExpandedSectionArrowOverlay style={{ top: expandedAnchorY }}>
          <ScrollArrowSlot $side="start" $hidden={isScrollArrowHidden('left')}>
            <ScrollArrowButton onClick={handleScrollLeft} aria-label={scrollLeftAriaLabel}>
              <LineAngleLeftIcon rtlFlip={false} />
            </ScrollArrowButton>
          </ScrollArrowSlot>
          <ScrollArrowSlot $side="end" $hidden={isScrollArrowHidden('right')}>
            <ScrollArrowButton onClick={handleScrollRight} aria-label={scrollRightAriaLabel}>
              <LineAngleRightIcon rtlFlip={false} />
            </ScrollArrowButton>
          </ScrollArrowSlot>
        </ExpandedSectionArrowOverlay>
      )}
      <StickyHeaderOverlay aria-hidden>
        <StickyHeaderScrollTrack ref={stickyHeaderScrollRef}>
          <HeaderTableWrapper $isPainted style={{ minWidth: tableMinInlineWidth, maxWidth: tableMaxInlineWidth, marginInline: tableMaxInlineWidth ? 'auto' : undefined }}>
            <StyledTable>
              <colgroup>
                <col style={{ width: STICKY_DATE_COL_WIDTH }} />
                {fareClassColumns.map(fc => <col key={fc} style={fareColWidthStyle} />)}
              </colgroup>
              <HeaderThead>
                {renderHeaderRow()}
              </HeaderThead>
            </StyledTable>
          </HeaderTableWrapper>
        </StickyHeaderScrollTrack>
      </StickyHeaderOverlay>
      <HorizontalScrollTrack ref={horizontalScrollRef} onScroll={syncStickyHeaderScroll}>
        <HeaderTableWrapper style={{ minWidth: tableMinInlineWidth, maxWidth: tableMaxInlineWidth, marginInline: tableMaxInlineWidth ? 'auto' : undefined }}>
          <StyledTable>
            <colgroup>
              <col style={{ width: STICKY_DATE_COL_WIDTH }} />
              {fareClassColumns.map(fc => <col key={fc} style={fareColWidthStyle} />)}
            </colgroup>
            <HeaderThead>
              {renderHeaderRow()}
            </HeaderThead>
          </StyledTable>
        </HeaderTableWrapper>
        <StyledTable style={{ minWidth: tableMinInlineWidth, maxWidth: tableMaxInlineWidth, marginInline: tableMaxInlineWidth ? 'auto' : undefined }}>
          <tbody>
            <tr aria-hidden>
              <TileGapTd colSpan={1 + fareColCount} />
            </tr>
          </tbody>
        </StyledTable>
        {itemsToShow.map((item, rowIndex) => {
          const hotelByCabinForRow = getHotelPriceByCabinForDate(props.hotelPriceByCabinAndDate, item.checkIn)
          const isRowExpanded = expandedDateId === item.id
          const isRowHighlighted = highlightedDateId === item.id
          const isRowActive = isRowExpanded || isRowHighlighted || item.id === selectedOptionId

          return (
            <Fragment key={item.id}>
              <RowTableWrapper
                className={isRowActive ? 'active' : undefined}
                style={{ minWidth: tableMinInlineWidth, maxWidth: tableMaxInlineWidth, marginInline: tableMaxInlineWidth ? 'auto' : undefined }}
              >
                <StyledTable>
                  <colgroup>
                    <col style={{ width: STICKY_DATE_COL_WIDTH }} />
                    {fareClassColumns.map(fc => <col key={fc} style={fareColWidthStyle} />)}
                  </colgroup>
                  <CardTbody>
                    <TableRow
                      item={item}
                      nights={nights}
                      rooms={rooms}
                      selectedOptionId={selectedOptionId}
                      isHighlighted={isRowHighlighted}
                      onOptionSelect={onOptionSelect}
                      fareClassColumns={fareClassColumns}
                      fareColumnCellStyle={fareColWidthStyle}
                      pageFareClass={fareClass}
                      seatEligiblePassengers={seatEligiblePassengers}
                      priceMode={props.priceMode}
                      luxPlusEnabled={luxPlusEnabled}
                      loadingLabel={loadingLabel}
                      rewardCashAddOnLabel={rewardCashAddOnLabel}
                      loginToViewLabel={loginToViewLabel}
                      enrollLabel={enrollLabel}
                      isLoggedIn={isLoggedIn}
                      hasLoyaltyAccount={hasLoyaltyAccount}
                      isInfiniteMember={isInfiniteMember}
                      onLoginClick={handleLoginClick}
                      onEnrollClick={handleEnrollClick}
                      departMelbourneLabel={departMelbourneLabel}
                      departMaleLabel={departMaleLabel}
                      noSeatsAvailableLabel={noSeatsAvailableLabel}
                      dayOffsetText={dayOffsetText}
                      prefetchedBundleData={props.itemBundleDataByDate?.[item.checkIn]}
                      hotelByCabinForRow={hotelByCabinForRow}
                      isExpanded={isRowExpanded}
                      onHighlight={handleHighlight}
                      onToggleExpand={handleToggleExpand}
                      selectFareLabel={selectFareLabel}
                      selectedFareLabel={selectedFareLabel}
                      showDetailsLabel={showDetailsLabel}
                      hideDetailsLabel={hideDetailsLabel}
                      fromLabel={fromLabel}
                      priceSuffixLabel={priceSuffixLabel}
                      expandedAnchorRef={isRowExpanded ? expandedAnchorRef : undefined}
                      feederOriginAirportCode={feederOriginAirportCode}
                    />
                  </CardTbody>
                </StyledTable>
              </RowTableWrapper>
              {rowIndex < itemsToShow.length - 1 && (
                <StyledTable style={{ minWidth: tableMinInlineWidth, maxWidth: tableMaxInlineWidth, marginInline: tableMaxInlineWidth ? 'auto' : undefined }}>
                  <tbody>
                    <tr aria-hidden>
                      <TileGapTd colSpan={1 + fareColCount} />
                    </tr>
                  </tbody>
                </StyledTable>
              )}
            </Fragment>
          )
        })}
      </HorizontalScrollTrack>
      {hasMore && (
        <ShowMoreContainer>
          <TextButton variant="default" kind="secondary" onClick={onShowMore}>
            {showMoreDatesText}
          </TextButton>
        </ShowMoreContainer>
      )}
    </TableWrap>
  )
})

export default MaldivesCharterTripOptionsTable
